/* Linden — picking products inside a quote request.

   Typing a list of references into a textarea means leaving the page to go and
   read the catalogue, which loses everything already filled in. The picker puts
   the catalogue beside the field instead: a thumbnail, a name, a price, a
   quantity box.

   It writes into the message field rather than posting its own, so there is one
   list in the email and not two that can disagree. Once someone edits that field
   by hand the picker stops writing to it — a control that silently overwrites
   what you just typed is worse than no control.

   Hidden until this file runs, so a browser without JavaScript sees the plain
   textarea and nothing broken. That matters more here than elsewhere: a public
   buyer is often on a locked-down machine inside an institution. */

class QuotePicker extends HTMLElement {
  connectedCallback() {
    this.field = document.getElementById(this.dataset.target);
    if (!this.field) return;

    this.rows = [...this.querySelectorAll('[data-picker-item]')];
    this.search = this.querySelector('[data-picker-search]');
    this.empty = this.querySelector('[data-picker-empty]');
    this.written = '';

    this.addEventListener('input', (event) => {
      if (event.target.matches('[data-picker-qty]')) this.compose();
      if (event.target === this.search) this.filter();
    });

    /* Every box starts at 0 so the column reads as one thing rather than a
       ragged mix of blanks and numbers. That would cost a keystroke — the zero
       cleared before typing — so focusing a box empties it.

       Emptying rather than selecting: the selection API does not apply to
       input[type=number], so select() is a no-op in a strict engine and there is
       no way to tell from script whether it took. Clearing the value works the
       same everywhere and needs nothing to be true about the browser. */
    this.addEventListener('focusin', (event) => {
      const box = event.target;
      if (box.matches?.('[data-picker-qty]') && box.value === '0') box.value = '';
    });

    /* And a box emptied by hand goes back to 0 rather than staying blank, or the
       tidiness lasts exactly until someone changes their mind. */
    this.addEventListener('focusout', (event) => {
      const box = event.target.closest?.('[data-picker-qty]');
      if (box && box.value.trim() === '') {
        box.value = '0';
        this.compose();
      }
    });

    /* Anything typed into the field that this picker did not put there makes it
       the customer's text from then on. */
    this.field.addEventListener('input', () => {
      if (this.field.value !== this.written) this.owned = false;
    });

    this.hidden = false;
  }

  filter() {
    const needle = this.search.value.trim().toLowerCase();
    let shown = 0;

    for (const row of this.rows) {
      const match = !needle || row.dataset.title.toLowerCase().includes(needle);
      row.hidden = !match;
      if (match) shown += 1;
    }

    if (this.empty) this.empty.hidden = shown > 0;
  }

  compose() {
    if (this.owned === false) return;

    const lines = this.rows
      .map((row) => {
        const quantity = Number(row.querySelector('[data-picker-qty]').value);
        return quantity > 0 ? `${quantity} × ${row.dataset.title}` : null;
      })
      .filter(Boolean);

    this.written = lines.join('\n');
    this.owned = true;
    this.field.value = this.written;
  }
}

if (!customElements.get('quote-picker')) {
  customElements.define('quote-picker', QuotePicker);
}
